import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import { useAuth } from '@/contexts/AuthContext'
import { useHasModule } from '@/contexts/TenantConfigContext'
import type { UserLevel } from '@/types'

interface PrivateRouteProps {
  children: React.ReactNode
  maxUserLevel?: UserLevel   // 이 레벨 이하만 접근 가능
  requiredModule?: string    // 테넌트에 이 모듈이 활성화되어야 접근 가능
}

const ModuleGuard: React.FC<{ module: string; children: React.ReactNode }> = ({
  module,
  children,
}) => {
  const hasModule = useHasModule(module)
  if (!hasModule) return <Navigate to="/not-available" replace />
  return <>{children}</>
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  maxUserLevel = 9,
  requiredModule,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (user && user.userLevel > maxUserLevel) {
    return <Navigate to="/unauthorized" replace />
  }

  if (requiredModule) {
    return <ModuleGuard module={requiredModule}>{children}</ModuleGuard>
  }

  return <>{children}</>
}

export default PrivateRoute
